import React from 'react';
import Icon from '../../../common/Icon/Icon';
import { formatDate, formatDateTime, formatCurrency } from '../../../../utils';
import { STATUS_LABELS, STATUS_ICON, normalizeStatus } from '../paymentDetails.constants';
import styles from './PayloadView.module.css';

interface StripePayloadViewProps {
  payload: Record<string, unknown>;
  currentStatus?: string;
  currentCurrency?: 'BRL' | 'USD';
}

const STATUS_MAP: Record<string, string> = {
  draft: 'PENDING',
  open: 'PENDING',
  paid: 'PAID',
  unpaid: 'EXPIRED',
  uncollectible: 'FAILED',
  void: 'REMOVED',
  voided: 'REMOVED',
};

const StripePayloadView: React.FC<StripePayloadViewProps> = ({ payload, currentStatus, currentCurrency }) => {
  const rawStatus = (payload.status as string) ?? '';
  const normalized = currentStatus
    ? normalizeStatus(currentStatus)
    : (STATUS_MAP[rawStatus] ?? normalizeStatus(rawStatus));
  const statusLabel = STATUS_LABELS[normalized] ?? rawStatus;
  const statusIcon = STATUS_ICON[normalized] ?? { name: 'help', color: 'var(--color-solid-info)' };

  const amountDue = payload.amount_due as number | undefined;
  const amountPaid = payload.amount_paid as number | undefined;
  const currency = ((payload.currency as string)?.toUpperCase() || currentCurrency || 'BRL') as 'BRL' | 'USD';
  const hostedUrl = payload.hosted_invoice_url as string | undefined;
  const invoicePdf = payload.invoice_pdf as string | undefined;
  const customerName = payload.customer_name as string | undefined;
  const customerEmail = payload.customer_email as string | undefined;
  const number = payload.number as string | undefined;
  const collectionMethod = payload.collection_method as string | undefined;
  const billingReason = payload.billing_reason as string | undefined;
  const created = payload.created as number | undefined;
  const dueDate = payload.due_date as number | undefined;

  const statusTransitions = payload.status_transitions as Record<string, number | null> | undefined;
  const paidAt = statusTransitions?.paid_at;
  const finalizedAt = statusTransitions?.finalized_at;

  return (
    <div className={styles.container}>
      {number && <Row label="Número da fatura" value={number} />}
      <Row label="ID" value={payload.id as string} />
      {customerName && <Row label="Cliente" value={customerName} />}
      {customerEmail && <Row label="E-mail" value={customerEmail} />}
      <Row label="Moeda" value={currency} />
      {amountDue != null && <Row label="Valor cobrado" value={formatCurrency(amountDue / 100, currency)} />}
      {amountPaid != null && amountPaid > 0 && <Row label="Valor pago" value={formatCurrency(amountPaid / 100, currency)} />}
      <Row
        label="Status"
        element={
          <span className={styles.status}>
            <Icon name={statusIcon.name} size={16} filled color={statusIcon.color} />
            {statusLabel}
          </span>
        }
      />
      {collectionMethod && <Row label="Método de cobrança" value={collectionMethod === 'send_invoice' ? 'Fatura enviada' : collectionMethod} />}
      {billingReason && <Row label="Motivo" value={billingReason === 'subscription_cycle' ? 'Ciclo de assinatura' : billingReason} />}
      {created && <Row label="Criado em" value={formatDate(new Date(created * 1000).toISOString())} />}
      {dueDate && <Row label="Vencimento" value={formatDate(new Date(dueDate * 1000).toISOString())} />}
      {finalizedAt && <Row label="Finalizado em" value={formatDate(new Date(finalizedAt * 1000).toISOString())} />}
      {paidAt && <Row label="Pago em" value={formatDateTime(new Date(paidAt * 1000).toISOString())} />}
      {hostedUrl && (
        <Row
          label="Link de pagamento"
          element={
            <a href={hostedUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="open_in_new" size={14} />
              Abrir fatura
            </a>
          }
        />
      )}
      {invoicePdf && (
        <Row
          label="PDF"
          element={
            <a href={invoicePdf} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="picture_as_pdf" size={14} />
              Baixar PDF
            </a>
          }
        />
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string; element?: React.ReactNode }> = ({ label, value, element }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    {element ?? <span className={styles.value}>{value ?? '—'}</span>}
  </div>
);

export default StripePayloadView;
